import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { X, Loader2, Save } from "lucide-react";

/**
 * DoctorProfileEditModal — редакция на личния профил на лекаря.
 * Полета: име, специалност, лицензен номер, клиника, адрес, телефон,
 *         институция, ORCID, LinkedIn, биография.
 */
export default function DoctorProfileEditModal({ open, onClose, initial, onSaved }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    specialty: "",
    license_number: "",
    clinic_name: "",
    clinic_address: "",
    phone: "",
    institution: "",
    orcid: "",
    linkedin: "",
    bio: "",
    // iter519 — personal measurements (doctor as Nutramilo user)
    sex: "",
    age: "",
    height_cm: "",
    weight_kg: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || "",
      specialty: initial?.profile?.specialty || "",
      license_number: initial?.profile?.license_number || "",
      clinic_name: initial?.profile?.clinic_name || "",
      clinic_address: initial?.profile?.clinic_address || "",
      phone: initial?.profile?.phone || "",
      institution: initial?.profile?.institution || "",
      orcid: initial?.profile?.orcid || "",
      linkedin: initial?.profile?.linkedin || "",
      bio: initial?.profile?.bio || "",
      sex: initial?.profile?.sex || initial?.sex || "",
      age: initial?.profile?.age ?? initial?.age ?? "",
      height_cm: initial?.profile?.height_cm ?? initial?.height_cm ?? "",
      weight_kg: initial?.profile?.weight_kg ?? initial?.weight_kg ?? "",
    });
  }, [open, initial]);

  if (!open) return null;

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error(bg ? "Името е задължително" : "Name is required");
      return;
    }
    setSaving(true);
    try {
      const token =
        localStorage.getItem("nutramilo_access_token") ||
        sessionStorage.getItem("nutramilo_access_token");
      await axios.patch(`${API}/doctor/profile`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(bg ? "Профилът е запазен" : "Profile saved");
      onSaved?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          (bg ? "Грешка при запис" : "Failed to save")
      );
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full h-10 px-3 rounded-lg border text-sm transition-colors
    ${isDark
      ? "bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-violet-400/60"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-violet-500"}
    focus:outline-none`;
  const labelCls = `text-[11px] font-semibold uppercase tracking-wider mb-1 block ${
    isDark ? "text-gray-400" : "text-gray-600"
  }`;

  // NOTE: Field rendered inline (NOT as a nested component) — defining a
  // component inside the parent causes a remount on every keystroke and the
  // input loses focus, which was the root cause of the iter319 doctor-profile
  // "input doesn't work" bug reported by users.
  const renderField = (id, label, opts = {}) => (
    <div key={id}>
      <label htmlFor={`dp-${id}`} className={labelCls}>{label}</label>
      <input
        id={`dp-${id}`}
        type={opts.type || "text"}
        value={form[id] ?? ""}
        onChange={(e) => setField(id, e.target.value)}
        placeholder={opts.placeholder}
        min={opts.min}
        max={opts.max}
        step={opts.step}
        className={inputCls}
        data-testid={`doctor-profile-input-${id}`}
      />
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="doctor-profile-edit-modal"
    >
      <div
        className={`w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-2xl
          ${isDark ? "bg-[hsl(222,47%,9%)] border border-white/10" : "bg-white border border-gray-200"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className={`sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b backdrop-blur
            ${isDark ? "bg-[hsl(222,47%,9%)]/95 border-white/10" : "bg-white/95 border-gray-200"}`}
        >
          <div>
            <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {bg ? "Редактиране на профила" : "Edit profile"}
            </h2>
            <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg
                ? "Тези данни се виждат от пациентите ви в направленията и SOAP бележките."
                : "These details appear on patient referrals and SOAP notes."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg ${
              isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-gray-100 text-gray-600"
            }`}
            aria-label={bg ? "Затвори" : "Close"}
            data-testid="doctor-profile-edit-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {renderField("name", bg ? "Пълно име" : "Full name", { placeholder: "Dr. ..." })}
          {renderField("specialty", bg ? "Специалност" : "Specialty", { placeholder: bg ? "Хирург, Ендокринолог..." : "Surgeon, Endocrinologist..." })}
          {renderField("license_number", bg ? "УИН / лиценз" : "License / UIN", { placeholder: "0000000000" })}
          {renderField("phone", bg ? "Телефон" : "Phone", { type: "tel", placeholder: "+359 ..." })}
          {renderField("clinic_name", bg ? "Клиника / практика" : "Clinic / practice")}
          {renderField("institution", bg ? "Институция" : "Institution")}
          <div className="sm:col-span-2">
            {renderField("clinic_address", bg ? "Адрес на клиниката" : "Clinic address")}
          </div>
          {renderField("orcid", "ORCID", { placeholder: "0000-0000-0000-0000" })}
          {renderField("linkedin", "LinkedIn", { placeholder: "https://linkedin.com/in/..." })}

          {/* iter519 — Personal health measurements (doctor as Nutramilo user) */}
          <div className="sm:col-span-2 mt-2">
            <div className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? "Лични измервания (за личните ви здравни анализи)" : "Personal measurements (for your own health analytics)"}
            </div>
          </div>
          <div>
            <label htmlFor="dp-sex" className={labelCls}>{bg ? "Пол" : "Sex"}</label>
            <select
              id="dp-sex"
              value={form.sex}
              onChange={(e) => setField("sex", e.target.value)}
              className={inputCls}
              data-testid="doctor-profile-input-sex"
            >
              <option value="">{bg ? "Изберете…" : "Select…"}</option>
              <option value="male">{bg ? "Мъж" : "Male"}</option>
              <option value="female">{bg ? "Жена" : "Female"}</option>
              <option value="other">{bg ? "Друг / не уточнявам" : "Other / prefer not to say"}</option>
            </select>
          </div>
          {renderField("age", bg ? "Възраст" : "Age", { type: "number", min: 18, max: 120, placeholder: "45" })}
          {renderField("height_cm", bg ? "Ръст (см)" : "Height (cm)", { type: "number", min: 100, max: 250, placeholder: "175" })}
          {renderField("weight_kg", bg ? "Тегло (кг)" : "Weight (kg)", { type: "number", min: 30, max: 300, step: "0.1", placeholder: "72" })}

          <div className="sm:col-span-2">
            <label htmlFor="dp-bio" className={labelCls}>
              {bg ? "Кратка биография" : "Short bio"}
            </label>
            <textarea
              id="dp-bio"
              value={form.bio}
              onChange={(e) => setField("bio", e.target.value)}
              rows={3}
              maxLength={1000}
              className={`${inputCls} h-auto py-2 resize-none`}
              placeholder={bg ? "Опит, публикации, фокус..." : "Experience, publications, focus..."}
              data-testid="doctor-profile-input-bio"
            />
            <div className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {form.bio.length}/1000
            </div>
          </div>
        </div>

        <footer
          className={`sticky bottom-0 flex items-center justify-end gap-2 px-5 py-3 border-t backdrop-blur
            ${isDark ? "bg-[hsl(222,47%,9%)]/95 border-white/10" : "bg-white/95 border-gray-200"}`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`h-9 px-4 rounded-lg text-sm font-semibold ${
              isDark ? "text-gray-300 hover:bg-white/5" : "text-gray-700 hover:bg-gray-100"
            }`}
            data-testid="doctor-profile-edit-cancel"
          >
            {bg ? "Отказ" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-semibold
              bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95 disabled:opacity-60"
            data-testid="doctor-profile-edit-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {bg ? "Запази" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}
